import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Navbar from '../Navbar';
import './index.css';

const ProjectDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { project } = location.state || {};

  // Refs for scroll reveal
  const headerRef = useRef(null);
  const leftSectionRef = useRef(null);
  const middleSection1Ref = useRef(null);
  const middleSection2Ref = useRef(null);
  const rightSectionRef = useRef(null);
  const backButtonRef = useRef(null);
  const ctaRef = useRef(null);

  // Intersection Observer for Scroll Reveal
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal-visible');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all sections
    const sections = [
      headerRef.current,
      leftSectionRef.current,
      middleSection1Ref.current,
      middleSection2Ref.current,
      rightSectionRef.current,
      backButtonRef.current,
      ctaRef.current
    ];

    sections.forEach(section => {
      if (section) {
        observer.observe(section);
      }
    });

    // Cleanup
    return () => {
      sections.forEach(section => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  // Handle navigation if no project data
  useEffect(() => {
    if (!project) {
      navigate('/projects');
    }
  }, [project, navigate]);

  // If no project data, show loading or return early after all hooks
  if (!project) {
    return (
      <div className="project-details-wrapper">
        <div className="loading-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Function to convert text to title case (capitalize each word)
  const formatText = (text) => {
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Format the type and category text
  const formattedType = formatText(project.type || project.projectType || "");
  const formattedCategory = formatText(project.category || "");

  // Generate project images array
  const projectImages = project.images || [];

  const isSixImageSpecial =
    project && (project.id === 14 || project.id === 40);

  // Map images into layout slots so different counts (3, 5, 6, etc.)
  // still feel intentional while using the same CSS grid structure.
  const layout = (() => {
    const images = projectImages;
    const n = images.length;

    if (n === 0) {
      return {};
    }

    const slots = {
      leftImage: images[0] || null,
      m1Wide: null,
      m1Small1: null,
      m1Small2: null,
      m2Wide: null,
      m2Small1: null,
      m2Small2: null,
      m2Tall: null,
      rightTall: null,
    };

    if (n === 1) {
      // Only hero image on the left
      return slots;
    }

    if (n === 2) {
      slots.m1Wide = images[1];
      return slots;
    }

    if (n === 3) {
      slots.m1Wide = images[1];
      slots.m1Small1 = images[2];
      return slots;
    }

    if (n === 4) {
      slots.m1Wide = images[1];
      slots.m1Small1 = images[2];
      slots.m1Small2 = images[3];
      return slots;
    }

    if (n === 5) {
      slots.m1Wide = images[1];
      slots.m1Small1 = images[2];
      slots.m1Small2 = images[3];
      // Use last image as tall right to keep composition strong
      slots.rightTall = images[4];
      return slots;
    }

    if (n === 6) {
      // Custom layout for specific 6-image interior projects
      if (isSixImageSpecial) {
        slots.m1Wide = images[1];
        slots.m1Small1 = images[2];
        slots.m1Small2 = images[3];
        // Second row: two tall images side by side
        slots.m2Tall = images[4];
        slots.rightTall = images[5];
        return slots;
      }

      // Default 6-image layout
      slots.m1Wide = images[1];
      slots.m1Small1 = images[2];
      slots.m1Small2 = images[3];
      slots.m2Wide = images[4];
      slots.m2Small1 = images[5];
      return slots;
    }

    if (n === 7) {
      slots.m1Wide = images[1];
      slots.m1Small1 = images[2];
      slots.m1Small2 = images[3];
      slots.m2Wide = images[4];
      slots.m2Small1 = images[5];
      slots.m2Small2 = images[6];
      return slots;
    }

    // 8 or more images – keep the original 8-image composition
    slots.m1Wide = images[1] || null;
    slots.m1Small1 = images[2] || null;
    slots.m1Small2 = images[3] || null;
    slots.m2Wide = images[4] || null;
    slots.m2Small1 = images[5] || null;
    slots.m2Small2 = images[6] || null;
    slots.rightTall = images[7] || null;

    return slots;
  })();

  return (
    <div className="project-details-wrapper">
      {/* Shared Navigation Header */}
      <Navbar />

      {/* Project Details Content */}
      <div className="project-details-container">
        <div ref={headerRef} className="project-header reveal-section reveal-fade">
          <h1>{project.title}</h1>
          <p>
            {formattedType}
            {formattedCategory && ` - ${formattedCategory} `}
          </p>
        </div>

        <div className="project-images-custom-grid">
          {/* Main Grid Container */}
          <div className="main-grid">

            {/* LEFT SECTION: Large Square Image */}
            {layout.leftImage && (
              <div
                ref={leftSectionRef}
                className="grid-section left-section reveal-section reveal-left"
              >
                <div className="grid-cell large-square">
                  <img src={layout.leftImage} alt="Project view 1" />
                </div>
              </div>
            )}

            {/* MIDDLE SECTION: Wide + 2 Small Images */}
            {(layout.m1Wide || layout.m1Small1 || layout.m1Small2) && (
              <div
                ref={middleSection1Ref}
                className="grid-section middle-section reveal-section reveal-right"
              >
                {layout.m1Wide && (
                  <div
                    className="grid-cell wide-rect reveal-scale"
                    style={{ transitionDelay: '0.1s' }}
                  >
                    <img src={layout.m1Wide} alt="Project view 2" />
                  </div>
                )}

                {(layout.m1Small1 || layout.m1Small2) && (
                  <div className="small-images-row">
                    {layout.m1Small1 && (
                      <div
                        className="grid-cell small-square reveal-scale"
                        style={{ transitionDelay: '0.2s' }}
                      >
                        <img src={layout.m1Small1} alt="Project view 3" />
                      </div>
                    )}
                    {layout.m1Small2 && (
                      <div
                        className="grid-cell small-square reveal-scale"
                        style={{ transitionDelay: '0.3s' }}
                      >
                        <img src={layout.m1Small2} alt="Project view 4" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* SECOND MIDDLE SECTION: Wide + 2 Small Images or Single Tall Image */}
            {(layout.m2Wide || layout.m2Small1 || layout.m2Small2 || layout.m2Tall) && (
              <div
                ref={middleSection2Ref}
                className="grid-section middle-section reveal-section reveal-left"
              >
                {/* Special case: single tall image fills this column */}
                {layout.m2Tall && !layout.m2Wide && !layout.m2Small1 && !layout.m2Small2 && (
                  <div
                    className="grid-cell tall-rect reveal-scale"
                    style={{ transitionDelay: '0.1s' }}
                  >
                    <img src={layout.m2Tall} alt="Project view 5" />
                  </div>
                )}

                {!layout.m2Tall && layout.m2Wide && (
                  <div
                    className="grid-cell wide-rect reveal-scale"
                    style={{ transitionDelay: '0.1s' }}
                  >
                    <img src={layout.m2Wide} alt="Project view 5" />
                  </div>
                )}

                {!layout.m2Tall && (layout.m2Small1 || layout.m2Small2) && (
                  <div className="small-images-row">
                    {layout.m2Small1 && (
                      <div
                        className="grid-cell small-square reveal-scale"
                        style={{ transitionDelay: '0.2s' }}
                      >
                        <img src={layout.m2Small1} alt="Project view 6" />
                      </div>
                    )}
                    {layout.m2Small2 && (
                      <div
                        className="grid-cell small-square reveal-scale"
                        style={{ transitionDelay: '0.3s' }}
                      >
                        <img src={layout.m2Small2} alt="Project view 7" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* RIGHT SECTION: Tall Rectangle Image */}
            {layout.rightTall && (
              <div
                ref={rightSectionRef}
                className="grid-section right-section reveal-section reveal-right"
              >
                <div className="grid-cell tall-rect">
                  <img src={layout.rightTall} alt="Project view 8" />
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Back to Projects Button */}
        <div ref={backButtonRef} className="back-button-container reveal-section reveal-bottom">
          <button
            className="back-to-projects-button"
            onClick={() =>
              navigate('/projects', {
                state: {
                  initialProjectType:
                    project.projectType === 'INTERIOR' || project.type === 'INTERIOR'
                      ? 'INTERIOR'
                      : 'ARCHITECTURE',
                },
              })
            }
          >
            ← BACK TO PROJECTS
          </button>
        </div>
      </div>

      {/* Let's Build Together CTA */}
      <section ref={ctaRef} className="build-together-cta reveal-section reveal-scale">
        <div className="build-together-cta__content">
          <h2 className="build-together-cta__title">Let's Build Meaningful Spaces Together</h2>
          <div className="build-together-cta__text">
            <p>
              From homes to workplaces, we craft architecture that inspires, belongs, and endures. Ready to <br /> begin your journey?
            </p>
          </div>
          <Link to="/contact" className="build-together-cta__button">
            START A CONVERSATION
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetails;